import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
	Box,
	Typography,
	Stack,
	IconButton,
	MenuItem,
	Select,
	SelectChangeEvent,
	Divider,
	FormControl,
	InputLabel,
	Grid,
	FormControlLabel,
	Radio,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import { ReactComponent as Delete } from 'common/assets/images/delete.svg';
import { ReactComponent as DownloadFile } from 'common/assets/images/download-file.svg';
import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import { ReactComponent as GreenTick } from 'common/assets/images/green-tick.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	BUTTON_LABELS,
	maxFileSizeAllowedInDataIngestion,
	CHOOSE_FILE_TYPE,
	PDF_LABEL,
	HTML_LABEL,
} from 'common/constants';
import {
	FILE_ALREADY_EXISTS_ERROR_MESSAGE,
	FILE_UPLOAD_DATA_TYPE_TEXT,
	FILE_UPLOAD_TITLE_TEXT,
	SUPPORTED_FILES_TEXT_EXCEL,
	SUPPORTED_FILES_TEXT_PDF,
	UPLOAD_YOUR_FILE_TEXT,
	UPLOAD_FILE_TYPES,
	DATA_INGESTION_RELATION_MAPPING_TAB_VALUES,
	REGULATIONS_UPLOAD_TEXT,
	REGULATIONS,
} from 'common/constants/DataIngestionConstants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { RootState } from 'common/store/AppStore';
import {
	setFileDrawer,
	setProcessingFileDataType,
	setProcessingFileName,
	setProcessingFilePath,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { setFileMappingData } from 'common/store/reducer/data-ingestion-reducer';
import {
	downloadBlobFileWithMultiFormat,
	toSentenceCase,
	urlWithTenant,
} from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { useUploadForm } from 'hooks/useUploadForm';

import { deleteUploadedFile, downloadFileTemplate } from '../api';
import { checkFileType, handleUploadResponse } from '../DataIngestionUtils';

import FileDrop from './FileDrop';

import styles from './FileUpload.module.scss';

interface FileUploadCompProps {
	onStartProcessing: (dataType: string) => void;
}

const {
	RISKS,
	CONTROLS,
	POLICY,
	PROCESSES,
	POLICIES,
	PRC_MAPPING,
	RELATION_MAPPING_TEXT_VALUE,
} = DATA_INGESTION_RELATION_MAPPING_TAB_VALUES;
const { OK } = HTTP_STATUS_CODES;

const { dataIngestion, uploadParser } = rrcpDashboardRoutes;
const FileUploadComp = (props: FileUploadCompProps) => {
	const { onStartProcessing } = props;
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const fileDrawerState = useSelector(
		(state: RootState) => state.common.fileDrawer,
	);
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);

	const { IS_REG_PARSER_ENABLED } = FeatureFlagsKeysEnum;

	const isRegParserEnabled = useFeatureFlag(
		IS_REG_PARSER_ENABLED,
		isLDResponseExists,
	);

	const [fileDrawer, setFileDrawerState] = useState(fileDrawerState);
	const [dataType, setDataType] = useState('');
	const [fileData, setFileData] = useState<File[]>([]);
	const [isLoading, setLoading] = useState<boolean>(false);
	const [progressActive, setProgressActive] = useState(false);
	const [showStartProcessingButton, setShowStartProcessingButton] =
		useState<boolean>(true);
	const [isStartProcessingClicked, setStartProcessing] =
		useState<boolean>(false);
	const [isFileUploadClicked, setFileUploadClick] = useState<boolean>(false);
	const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<
		string | undefined
	>(undefined);
	const [selectedUploadFileType, setSelectedUploadFileType] =
		useState<string>('');

	const [uploadBtnSelected, setUploadBtnSelected] = useState<boolean>(false);
	const { drawerCloseIconColor } = styles;
	const displayDataType =
		dataType === 'relationship_mapping' ? PRC_MAPPING : dataType;
	const {
		uploadForm,
		progress: uploadProgress,
		cancelUpload,
		uploadResponse,
		uploadError,
		setUploadError,
		isSuccess,
	} = useUploadForm(`${process.env.REACT_APP_FILE_UPLOAD_SERVICE_URL}/upload`);

	const handleDrawerChange = useCallback((data: any, type: string) => {
		callDeleteApi(data?.name, type);
		setFileDrawerState(!fileDrawer);
		dispatch(setFileDrawer(!fileDrawer));
		dispatch(setFileMappingData({}));
	}, []);
	const { CANCEL, BACK, UPLOAD, START_PROCESSING, GET_STARTED } = BUTTON_LABELS;
	const deleteAttachment = async (btnSelected: boolean) => {
		if (btnSelected) {
			callDeleteApi(fileData[0]?.name, dataType);
		}

		setFileData([]);
	};

	const handleDataTypeChange = useCallback((e: SelectChangeEvent) => {
		setFileData([]);
		setShowStartProcessingButton(true);
		setSelectedUploadFileType('');
		setDataType(e.target.value);
	}, []);

	const handleFileData = useCallback((data: File[], errorMessage?: string) => {
		setFileData(data);
		setUploadBtnSelected(false);
		setFileUploadErrorMessage(errorMessage);
	}, []);

	const cancelClickHandler = useCallback((data: any, type: string) => {
		callDeleteApi(data?.name, type);
		cancelUpload();
	}, []);

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				isToastOpen: false,
				message: '',
				severity: '',
			}),
		);
	}, []);

	const onUploadClickHandler = useCallback(async () => {
		setLoading(true);
		setProgressActive(true);
		setUploadBtnSelected(true);
		setFileUploadClick(true);
		const fd = new FormData();
		fd.append('dataType', dataType);
		fd.append('file', fileData[0]);
		dispatch(setProcessingFileDataType(dataType));
		dispatch(setProcessingFilePath(fileData[0]?.webkitRelativePath));
		dispatch(setProcessingFileName(fileData[0]?.name));
		await uploadForm(fd);
	}, [dataType, fileData, loggedInUser]);

	useEffect(() => {
		if (dataType !== '' && fileData.length) {
			checkFileType(dataType, selectedUploadFileType, fileData, dispatch);

			setShowStartProcessingButton(false);
		}
	}, [dataType, fileData]);

	useEffect(() => {
		handleUploadResponse({
			uploadResponse,
			uploadError,
			dispatch,
			setShowStartProcessingButton,
			setLoading,
			setFileUploadClick,
			setStartProcessing,
		});
	}, [uploadResponse, uploadError, dispatch]);

	useEffect(() => {
		if (isSuccess) {
			if (dataType === POLICIES) {
				dispatch(
					setToastMessage({
						isToastOpen: true,
						message: `File Uploaded Successfully.`,
						severity: 'success',
					}),
				);
				setLoading(false);
				setFileUploadClick(false);
				setStartProcessing(false);

				setProgressActive(false);
				handleFileData([]);
				onStartProcessing(dataType);
			}
		}
	}, [isSuccess]);
	useEffect(() => {
		const cancelRequest = async () => {
			callDeleteApi(fileData[0].name, dataType);
		};

		if (uploadError && uploadError?.message === 'canceled') {
			cancelRequest();
		} else if (
			uploadError &&
			uploadError?.errorMessage?.includes('Blob already exists with the name')
		) {
			dispatch(
				setToastMessage({
					isToastOpen: true,
					message:
						uploadError?.errorMessage.includes(
							'Blob already exists with the name',
						) && FILE_ALREADY_EXISTS_ERROR_MESSAGE,
					severity: 'error',
				}),
			);
		}
		setLoading(false);
		setFileUploadClick(false);
		setStartProcessing(false);
		setUploadBtnSelected(false);
		handleFileData([]);
		setProgressActive(false);
		setUploadError(null);
		setShowStartProcessingButton(true);
	}, [uploadError]);

	const callDeleteApi = async (data: string, type: string) => {
		if (data) {
			await deleteUploadedFile(type, data);
		}
	};

	const downloadTemplateFile = async () => {
		if (!dataType || dataType === POLICIES) {
			return;
		}
		const fileName = `${dataType}_template.xlsx`.toLowerCase();
		const response = await downloadFileTemplate(
			fileName,
			dataType.toLowerCase(),
		);
		if (response?.statusCode === OK) {
			const responseData: any = response?.responseBody;
			downloadBlobFileWithMultiFormat(responseData, fileName);
			dispatch(
				setToastMessage({
					isToastOpen: true,
					message: `${displayDataType} template downloaded Successfully.`,
					severity: 'success',
				}),
			);
		} else {
			dispatch(
				setToastMessage({
					isToastOpen: true,
					message: `Failed to download the ${displayDataType} template.`,
					severity: 'error',
				}),
			);
		}
	};

	const getFileTypeInfo = () => {
		let csvOrXlsText;
		if (selectedUploadFileType === PDF_LABEL || dataType === POLICIES) {
			csvOrXlsText = SUPPORTED_FILES_TEXT_PDF;
		} else {
			csvOrXlsText = SUPPORTED_FILES_TEXT_EXCEL;
		}

		const fileText = `You can upload ${csvOrXlsText} files. The max file size is ${maxFileSizeAllowedInDataIngestion} MB`;

		return fileText;
	};
	const getAcceptedFileTypeData = () => {
		let result = {};
		const defaultAcceptType = {
			'application/vnd.ms-excel': ['.xls'],
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
				'.xlsx',
			],
		};

		switch (dataType) {
			case POLICIES:
				result = { 'application/pdf': ['.pdf'] };
				break;
			case REGULATIONS:
				result = {
					'application/vnd.ms-excel': ['.xls'],
					'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
						'.xlsx',
					],
					'application/pdf': ['.pdf'],
				};
				break;

			default:
				result = defaultAcceptType;
				break;
		}

		return result;
	};
	const onGetStarted = () => {
		dispatch(setProcessingFileDataType(dataType));
		dispatch(setProcessingFilePath(fileData[0]?.webkitRelativePath));
		dispatch(setProcessingFileName(fileData[0]?.name));
		setFileDrawerState(!fileDrawer);
		dispatch(setFileDrawer(!fileDrawer));
		navigate(urlWithTenant(`/${dataIngestion}/${uploadParser}`));
	};

	return (
		<Box role="presentation" data-testid="fileupload-container">
			<div
				className={styles['fileupload-text-background']}
				data-testid="fileupload-top-div"
			>
				<Stack
					direction="row"
					justifyContent="space-between"
					spacing={2}
					className={`${
						isLoading && !isFileUploadClicked && !isStartProcessingClicked
							? styles['fileupload-close-hidden']
							: styles['fileupload-close-visible']
					}`}
					data-testid="close-button-stack"
				>
					<Typography variant="h1" className={styles['fileupload-text-name']}>
						{FILE_UPLOAD_TITLE_TEXT}
					</Typography>
					<IconButton
						size="large"
						onClick={() => {
							handleDrawerChange(fileData[0], dataType);
						}}
						data-testid="close-button-icon"
						className={styles['fileupload-close']}
					>
						<DrawerClose fill={drawerCloseIconColor} />
					</IconButton>
				</Stack>
			</div>
			<Divider />
			<Stack className={styles['fileupload-first-container']}>
				<Typography className={styles['fileupload-header-text']}>
					{FILE_UPLOAD_DATA_TYPE_TEXT}
				</Typography>
				<FormControl data-testid="form-control">
					<InputLabel
						id="demo"
						sx={{
							'&.Mui-focused': {
								marginTop: '10px',
							},
						}}
						className={dataType && styles['fileupload-select']}
					>
						Data type
						{!dataType && (
							<span className={styles['fileupload-datatype-required']}> *</span>
						)}
					</InputLabel>
					<Select
						disabled={isLoading}
						onChange={handleDataTypeChange}
						IconComponent={ExpandMoreRoundedIcon}
						labelId="demo"
						sx={{
							zIndex: 1300,
						}}
						value={dataType}
						inputProps={{
							'data-testid': 'datatype-ele',
						}}
					>
						<MenuItem value={CONTROLS} data-testid={CONTROLS}>
							{toSentenceCase(CONTROLS)}
						</MenuItem>
						<MenuItem value="Regulations" data-testid="Regulations">
							{toSentenceCase('Regulations')}
						</MenuItem>

						<MenuItem value={RISKS}>{toSentenceCase(RISKS)}</MenuItem>
						<MenuItem value={POLICIES}>{toSentenceCase(POLICY)}</MenuItem>
						<MenuItem value={PROCESSES}>{toSentenceCase(PROCESSES)}</MenuItem>
						<MenuItem value={RELATION_MAPPING_TEXT_VALUE}>
							{PRC_MAPPING}
						</MenuItem>
					</Select>
				</FormControl>
				<Stack>
					{dataType === REGULATIONS && isRegParserEnabled && (
						<Stack pt={2}>
							<Typography className={styles['fileupload-text']} pb={1}>
								{REGULATIONS_UPLOAD_TEXT}
							</Typography>
							<Typography className={styles['fileupload-choosetype-text']}>
								{CHOOSE_FILE_TYPE}
							</Typography>
							<Stack direction="row">
								{UPLOAD_FILE_TYPES.map((ele: any) => (
									<FormControl
										component="fieldset"
										key={ele?.value}
										data-testid="regulations-container"
									>
										<FormControlLabel
											value={ele?.value}
											control={
												<Radio
													checked={ele?.value === selectedUploadFileType}
													disabled={ele?.value === HTML_LABEL}
													onChange={() => {
														setSelectedUploadFileType(ele?.value);
													}}
												/>
											}
											data-testid="file-type"
											label={ele?.label}
										/>
									</FormControl>
								))}
							</Stack>
						</Stack>
					)}
				</Stack>
				<Typography className={styles['fileupload-header-text']}>
					{UPLOAD_YOUR_FILE_TEXT}
				</Typography>
				<Typography className={styles['fileupload-max-size']}>
					{getFileTypeInfo()}
				</Typography>
				<Grid
					className={styles['filedrop-padding']}
					justifyContent="space-between"
					direction="row"
				>
					<FileDrop
						accept={getAcceptedFileTypeData()}
						handleFileData={handleFileData}
						fileData={fileData}
						isLoading={isLoading}
						progress={uploadProgress}
						maxfileSize={maxFileSizeAllowedInDataIngestion}
						progressActive={progressActive}
						setProgressActive={setProgressActive}
						isSuccess={isSuccess}
						dataType={dataType}
						setShowStartProcessingButton={setShowStartProcessingButton}
					/>
					{fileData.length > 0 &&
						fileData.some((file) => file.size < 10 * 1024 * 1024) && (
							<GreenTick className={styles['notification-success']} />
						)}
				</Grid>

				<Grid item>
					{fileData.length > 0 && (
						<Typography data-testid="delete-attachment">
							<span
								data-testid="delete-attachment-ele"
								onClick={() => {
									deleteAttachment(uploadBtnSelected);
								}}
								role="presentation"
								className={styles['delete-attachment']}
							>
								Delete Attachment
							</span>
						</Typography>
					)}
				</Grid>
				{fileUploadErrorMessage && (
					<Grid
						container
						direction="row"
						className={styles['file-upload-error-container']}
					>
						<Grid item display="flex" gap="10px">
							<Delete />
							<Grid item xs zeroMinWidth>
								<Typography className={styles['file-upload-error-header']}>
									Alert
								</Typography>
								<Typography className={styles['file-upload-error-sub-header']}>
									{fileUploadErrorMessage}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
				)}
			</Stack>
			<Stack className={styles['fileupload-btn-position']}>
				<Divider className={styles['fileupload-divider-position']} />
				<Stack
					direction="row"
					spacing={2}
					className={styles['fileupload-btn-group']}
					data-testid="close-button-stack"
				>
					<Grid container direction="row">
						<Grid
							item
							xs={6}
							gap={0.5}
							display="flex"
							alignItems="center"
							className={
								!dataType || dataType === POLICIES
									? styles['filedownload-disabled']
									: undefined
							}
						>
							<DownloadFile
								className={
									dataType === REGULATIONS &&
									selectedUploadFileType === PDF_LABEL
										? styles['fileupload-download-no-icon']
										: styles['fileupload-download-file-icon']
								}
							/>
							<Typography
								onClick={downloadTemplateFile}
								className={
									dataType === REGULATIONS &&
									selectedUploadFileType === PDF_LABEL
										? styles['fileupload-download-no-icon']
										: styles['fileupload-download-file']
								}
							>
								Download {displayDataType} Template
							</Typography>
						</Grid>
						<Grid item display="flex" gap={1} justifyContent="flex-end" xs={6}>
							{isLoading && progressActive ? (
								<ButtonComp
									btnType="primary"
									clickHandler={() => {
										cancelClickHandler(fileData[0], dataType);
									}}
									data-testid="fileupload-cancel-btn"
								>
									{CANCEL}
								</ButtonComp>
							) : (
								<ButtonComp
									btnType="secondary"
									clickHandler={() => {
										handleDrawerChange(fileData[0], dataType);
									}}
								>
									{BACK}
								</ButtonComp>
							)}

							{!showStartProcessingButton &&
								!isLoading &&
								selectedUploadFileType !== PDF_LABEL &&
								selectedUploadFileType !== HTML_LABEL && (
									<ButtonComp
										btnType="primary"
										clickHandler={onUploadClickHandler}
										disabled={
											dataType === '' || !fileData || fileData.length === 0
										}
										data-testid="next-btn"
									>
										{UPLOAD}
									</ButtonComp>
								)}
							{showStartProcessingButton &&
								!isLoading &&
								selectedUploadFileType !== PDF_LABEL &&
								selectedUploadFileType !== HTML_LABEL && (
									<ButtonComp
										btnType="primary"
										clickHandler={() => {
											onStartProcessing(dataType);
										}}
										disabled={
											dataType === '' ||
											!fileData ||
											fileData.length === 0 ||
											dataType === POLICIES
										}
										compClass={styles['fileupload-getstarted-btn-text']}
										data-testid="next-proceesing-btn"
									>
										{START_PROCESSING}
									</ButtonComp>
								)}
							{(selectedUploadFileType === PDF_LABEL ||
								selectedUploadFileType === HTML_LABEL) &&
								!isLoading && (
									<ButtonComp
										btnType="primary"
										clickHandler={onGetStarted}
										compClass={styles['fileupload-getstarted-btn-text']}
										disabled={
											dataType === '' || !fileData || fileData.length === 0
										}
										data-testid="getstarted-btn"
									>
										{GET_STARTED}
									</ButtonComp>
								)}
						</Grid>
					</Grid>
				</Stack>
			</Stack>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</Box>
	);
};

export default FileUploadComp;
